import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '../lib/api-client'
import { ApiError } from '../lib/errors'
import type { ExportStatusResponse } from '../../../../types'

// How often the page re-checks a preparing export. This is most of the
// answer to "the polling UI cannot hammer the status endpoint": a fixed,
// generous interval and one in-flight request at a time (see `inFlight`
// below) — an export that takes an hour makes ~900 requests over that hour,
// not one per render or one per open tab per second. POLL_TIMEOUT_MS below
// is the other half.
const POLL_INTERVAL_MS = 4000

// Bounds how long this page will keep asking, even if every poll succeeds
// and just keeps saying `preparing` — a genuinely wedged job would otherwise
// be polled every 4s forever, indefinitely, per open tab. 30 minutes matches
// EXPORT_LOCK_TTL_SECONDS (cloudflare/workers/utils/export-lock.ts): past
// that, the export lock backing this job has already expired server-side,
// so there is no legitimate way for it to still be genuinely running.
const POLL_TIMEOUT_MS = 30 * 60 * 1000

type ViewState =
  | { kind: 'loading' }
  | { kind: 'preparing'; progress: { bytesWritten: number; totalBytes: number } }
  | { kind: 'ready'; url: string; expiresAt: number; omitted: number }
  | { kind: 'failed'; reason: string }
  | { kind: 'not-found' }
  | { kind: 'denied' }
  | { kind: 'timed-out' }

function toViewState(data: ExportStatusResponse): ViewState {
  if (data.status === 'preparing') return { kind: 'preparing', progress: data.progress }
  if (data.status === 'ready') {
    return { kind: 'ready', url: data.url, expiresAt: data.expiresAt, omitted: data.omitted }
  }
  return { kind: 'failed', reason: data.reason }
}

function progressPercent(progress: { bytesWritten: number; totalBytes: number }): number {
  if (progress.totalBytes <= 0) return 0
  return Math.min(100, Math.round((progress.bytesWritten / progress.totalBytes) * 100))
}

/**
 * `/album/:albumId/export/:hash` — the page an "your download is ready"
 * email links to, and the page album-list.tsx sends someone to the moment
 * they start an async export. Both paths land here on purpose: the email
 * never carries a download link (see sendExportReady's doc comment,
 * cloudflare/workers/utils/postmark.ts), only this authenticated app URL,
 * which is what actually calls Task 7 and mints one.
 *
 * There is no cancel control here. The backend has no cancel endpoint (the
 * 409 an in-progress export returns no longer promises one either) — an
 * export either finishes, fails, or is still running, and this page only
 * ever reports which.
 */
export default function ExportStatus() {
  const { albumId, hash } = useParams<{ albumId: string; hash: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<ViewState>({ kind: 'loading' })
  // Guards against a slow response landing after the interval has already
  // fired again — without this, a status check that takes longer than
  // POLL_INTERVAL_MS would pile up overlapping requests instead of just
  // running one at a time.
  const inFlight = useRef(false)
  // When THIS attempt at watching the job started. Reset on a manual retry
  // from the `timed-out` state, so "check again" genuinely restarts the
  // clock rather than being permanently stuck once tripped once.
  const startedAtRef = useRef<number>(Date.now())
  // Has a `failed` already been seen, uninterrupted, on this watch?
  //
  // `failed` is the one terminal render that stops polling forever, and it is
  // reachable from a job that is about to be fine. /download/init's rebuild
  // path calls `instance.restart()` on a TERMINAL instance and answers
  // `preparing`; the page navigates here and polls, and every poll that lands
  // before that instance's status flips reports terminal — so a single
  // unlucky tick renders "This download didn't finish" over a job that is
  // starting. Whether restart() flips the status synchronously is exactly
  // what cannot be verified under this harness (SOL-170, and the reason this
  // is treated as live risk rather than theory).
  //
  // So the FIRST `failed` of a run buys one more interval instead of the
  // dead-end render, and only a second consecutive one commits. Cheap in the
  // genuine-failure case (one extra 4s tick before the same page), and it
  // covers every transiently-terminal read, not just the restart race. The
  // alternative — having handleExportStatus suppress `failed` while a
  // `.request.json` is newer than the instance's finish — cannot be built as
  // specified: `InstanceStatus` is `{status, error, output}` and carries no
  // finish timestamp, so the server would have to substitute a wall-clock
  // window over the request object's R2 upload time, i.e. a second, weaker
  // clock, when the client already owns a polling one.
  const sawFailedRef = useRef(false)

  const check = useCallback(async () => {
    if (!albumId || !hash || inFlight.current) return
    inFlight.current = true
    try {
      const data = await apiClient.getExportStatus(albumId, hash)
      // Applies even to a SUCCESSFUL `preparing` response: a wedged job can
      // keep answering "still preparing" forever without ever erroring, and
      // the timeout has to catch that case too, not just a failing one.
      if (data.status === 'preparing' && Date.now() - startedAtRef.current > POLL_TIMEOUT_MS) {
        setState({ kind: 'timed-out' })
        return
      }
      // One grace interval before committing to the dead-end (see
      // sawFailedRef). Returning WITHOUT setState leaves the page in
      // `loading`/`preparing`, which is what keeps the polling effect alive
      // for the next tick — the timeout ceiling above still bounds it.
      if (data.status === 'failed' && !sawFailedRef.current) {
        sawFailedRef.current = true
        return
      }
      // Consecutive, not cumulative: a `preparing` or `ready` in between means
      // the job was never terminal, so the next `failed` gets its own grace
      // interval rather than committing immediately on a second unrelated one.
      if (data.status !== 'failed') sawFailedRef.current = false
      setState(toViewState(data))
    } catch (err) {
      // 404/403 are genuinely permanent — no amount of retrying makes a
      // deleted export or a revoked membership come back — so these are the
      // only failures allowed to end polling immediately.
      if (err instanceof ApiError && err.isNotFoundError()) { setState({ kind: 'not-found' }); return }
      if (err instanceof ApiError && err.status === 403) { setState({ kind: 'denied' }); return }
      // Everything else (a network blip, a 503 while the album listing is
      // briefly unavailable) is treated as transient: a job that was
      // genuinely `preparing` a moment ago does not stop being so because
      // one poll failed. Collapsing that into a dead-end error page would be
      // worse than doing nothing this tick and letting the next scheduled
      // poll retry — UNLESS we're past the same 30-minute ceiling above,
      // in which case there is no longer a "known-good preparing" to
      // preserve either.
      if (Date.now() - startedAtRef.current > POLL_TIMEOUT_MS) {
        setState({ kind: 'timed-out' })
      }
    } finally {
      inFlight.current = false
    }
  }, [albumId, hash])

  useEffect(() => {
    if (!albumId || !hash) { setState({ kind: 'not-found' }); return }
    startedAtRef.current = Date.now()
    sawFailedRef.current = false
    setState({ kind: 'loading' })
    check()
  }, [albumId, hash, check])

  // Polls through BOTH `loading` (so a transient failure on the very first
  // check retries automatically instead of stranding the page on a blank
  // spinner) and `preparing`. Depends on state.kind, not state itself: a
  // progress update while preparing produces a new state OBJECT with the
  // same kind, and re-running this effect on every one of those would tear
  // down and recreate the interval on every poll instead of letting one
  // interval keep ticking.
  useEffect(() => {
    if (state.kind !== 'preparing' && state.kind !== 'loading') return
    const id = setInterval(check, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [state.kind, check])

  const checkAgain = () => {
    startedAtRef.current = Date.now()
    // A manual retry is a fresh watch, so the grace interval is fresh too --
    // otherwise "check again" from a timed-out page would commit to `failed`
    // on its very first response.
    sawFailedRef.current = false
    setState({ kind: 'loading' })
    check()
  }

  // Every terminal state's way back goes to the SPECIFIC album this export
  // came from, not just the dashboard root — album-list.tsx reads this from
  // `location.state` to open it directly (it has no URL-based routing of its
  // own; see the note there). Most concretely useful from `failed`, whose
  // server-supplied reason is often literally "request the download again."
  const backToAlbum = (
    <button
      type="button"
      className="secondary-button"
      onClick={() => navigate('/', { state: albumId ? { openAlbumId: albumId } : undefined })}
    >
      Back to the album
    </button>
  )

  if (state.kind === 'loading') {
    return (
      <div className="export-status">
        <h1 className="export-status__title">Checking your download…</h1>
      </div>
    )
  }

  if (state.kind === 'preparing') {
    const percent = progressPercent(state.progress)
    return (
      <div className="export-status">
        <h1 className="export-status__title">Preparing your download</h1>
        <p className="export-status__note">
          We're building your archive. This can take a while for a large album — feel free to
          close this tab, we'll email you the moment it's ready.
        </p>
        <div className="export-status__progress-track" role="progressbar"
          aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
          <div className="export-status__progress-fill" style={{ width: `${percent}%` }} />
        </div>
        <p className="export-status__percent">{percent}%</p>
      </div>
    )
  }

  if (state.kind === 'ready') {
    return (
      <div className="export-status">
        <h1 className="export-status__title">Your download is ready</h1>
        {state.omitted > 0 && (
          <p className="export-status__note">
            {state.omitted} {state.omitted === 1 ? 'photo' : 'photos'} added to this album after
            this download was built {state.omitted === 1 ? "isn't" : "aren't"} included. Start a
            new download to include {state.omitted === 1 ? 'it' : 'them'}.
          </p>
        )}
        {/* A plain navigation, not lightbox.tsx's fetch-into-a-blob pattern:
            `state.url` is a cross-origin presigned R2 URL (or, in dev, a
            same-origin stand-in — see mintExportLink's NODE_ENV branch),
            and `download` is only honoured same-origin/blob/data anyway.
            Buffering a multi-gigabyte archive through JS before saving it
            would be exactly the resumable-Range-request property this whole
            async tier exists to keep. R2's response already carries
            `Content-Disposition: attachment` (mintExportLink sets it), which
            is what actually triggers the save dialog here. */}
        <a className="primary-button export-status__download" href={state.url} download>
          Download archive
        </a>
        <p className="export-status__expiry">
          This link works until {new Date(state.expiresAt * 1000).toLocaleString()}.
        </p>
        {backToAlbum}
      </div>
    )
  }

  if (state.kind === 'failed') {
    return (
      <div className="export-status">
        <h1 className="export-status__title">This download didn't finish</h1>
        <p className="export-status__note">{state.reason}</p>
        {backToAlbum}
      </div>
    )
  }

  if (state.kind === 'not-found') {
    return (
      <div className="export-status">
        <h1 className="export-status__title">This download link doesn't work anymore</h1>
        <p className="export-status__note">
          It may have expired, or the album it pointed to may no longer exist.
        </p>
        {backToAlbum}
      </div>
    )
  }

  if (state.kind === 'denied') {
    return (
      <div className="export-status">
        <h1 className="export-status__title">You don't have access to this album</h1>
        {backToAlbum}
      </div>
    )
  }

  // 'timed-out': the only way polling stops without a definite answer.
  return (
    <div className="export-status">
      <h1 className="export-status__title">This is taking longer than expected</h1>
      <p className="export-status__note">
        We've been checking for a while with no result. The job may have stalled — check again,
        or come back later; we'll still email you if it finishes.
      </p>
      <button type="button" className="primary-button" onClick={checkAgain}>
        Check again
      </button>
      {backToAlbum}
    </div>
  )
}
